<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import AppHeader from '../components/AppHeader.vue'
  import CustomTag from '../components/CustomTag.vue'
  import { useFacturenStore } from '../stores/facturen'
  import type { Factuur, FactuurStatus } from '../../../../types'

  import Panel from 'primevue/panel'
  import Select from 'primevue/select'
  import IconField from 'primevue/iconfield'
  import InputIcon from 'primevue/inputicon'
  import InputText from 'primevue/inputtext'
  import Button from 'primevue/button'

  const router = useRouter()
  const facturenStore = useFacturenStore()

  const searchQuery = ref('')
  const statusFilter = ref('all')
  const rows = ref(12)
  const displayCount = ref(rows.value)

  const statusOptions = [
    { label: 'Alle', value: 'all' },
    { label: 'Concept', value: 'concept' },
    { label: 'Verzonden', value: 'verzonden' },
    { label: 'Betaald', value: 'betaald' },
    { label: 'Vervallen', value: 'vervallen' },
  ]

  const statusLabels: Record<FactuurStatus, string> = {
    concept: 'Concept',
    verzonden: 'Verzonden',
    betaald: 'Betaald',
    vervallen: 'Vervallen',
    gecrediteerd: 'Gecrediteerd',
  }

  const statusColors: Record<FactuurStatus, string> = {
    concept: 'nieuw',
    verzonden: 'aangepast',
    betaald: 'alles_geaccepteerd',
    vervallen: 'afgewezen',
    gecrediteerd: 'afgewezen',
  }

  const filteredFacturen = computed(() => {
    const items = facturenStore.facturen as Factuur[]

    return items.filter((factuur) => {
      if (statusFilter.value !== 'all' && factuur.status !== statusFilter.value) {
        return false
      }

      const query = searchQuery.value.trim().toLowerCase()
      if (!query) {
        return true
      }

      const opdrachtgever = factuur.opdrachtgever?.display_name || factuur.opdrachtgever?.email || ''
      return [factuur.factuurnr || '', factuur.opmerkingen || '', opdrachtgever].some((value) => value.toLowerCase().includes(query))
    })
  })

  const displayedFacturen = computed(() => filteredFacturen.value.slice(0, displayCount.value))
  const hasMore = computed(() => displayCount.value < filteredFacturen.value.length)

  function getStatusLabel(status: FactuurStatus) {
    return statusLabels[status] || status
  }

  function getStatusColor(status: FactuurStatus) {
    return statusColors[status] || 'info'
  }

  function formatDate(dateString: string | null) {
    if (!dateString) return '-'

    try {
      return new Intl.DateTimeFormat('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(dateString))
    } catch {
      return '-'
    }
  }

  function formatCurrency(amount: number | null | undefined) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0)
  }

  function openFactuur(id: string) {
    router.push({ name: 'FactuurDetails', params: { id } })
  }

  async function fetchFacturen() {
    await facturenStore.fetchFacturen({
      status: statusFilter.value === 'all' ? null : statusFilter.value,
      query: searchQuery.value || null,
    })
  }

  function loadMore() {
    displayCount.value = Math.min(displayCount.value + rows.value, filteredFacturen.value.length)
  }

  watch(filteredFacturen, (value) => {
    displayCount.value = Math.min(rows.value, value.length)
  })

  watch([searchQuery, statusFilter], () => {
    void fetchFacturen()
  })

  onMounted(() => {
    void fetchFacturen()
  })
</script>

<template>
  <div class="facturen-page">
    <AppHeader
      title="Facturen"
      :show-back-button="true"
      back-to="/"
      back-text="Terug naar dashboard"
    />

    <Panel
      class="filters-card"
      toggleable
      collapsed
      header="Zoeken en Filteren"
    >
      <div class="filters-stack">
        <div class="filters-search">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="searchQuery"
              placeholder="Zoek op factuurnummer, opdrachtgever of opmerking..."
              class="filters-input"
            />
          </IconField>
          <div
            v-if="searchQuery && filteredFacturen.length !== undefined"
            class="filters-meta"
          >
            {{ filteredFacturen.length }} resultaten gevonden
          </div>
        </div>

        <div class="filters-row">
          <div class="filters-select">
            <Select
              v-model="statusFilter"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter op status"
              class="w-full"
            />
          </div>

          <Button
            icon="pi pi-refresh"
            @click="fetchFacturen()"
            :loading="facturenStore.loading"
            severity="secondary"
            outlined
            aria-label="Vernieuwen"
            class="filters-refresh"
          />
        </div>
      </div>
    </Panel>

    <div
      v-if="facturenStore.loading"
      class="status-card status-loading"
    >
      <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>

    <div
      v-else-if="facturenStore.error"
      class="status-card status-error"
    >
      {{ facturenStore.error }}
    </div>

    <div
      v-else-if="filteredFacturen.length === 0"
      class="status-card status-empty"
    >
      <template v-if="searchQuery"> Geen facturen gevonden voor deze zoekopdracht. </template>
      <template v-else> Er zijn nog geen facturen beschikbaar. </template>
    </div>

    <div v-else>
      <div class="facturen-header">
        <span class="facturen-count">{{ filteredFacturen.length }} facturen gevonden</span>
        <Button
          icon="pi pi-refresh"
          @click="fetchFacturen()"
          :loading="facturenStore.loading"
          severity="secondary"
          outlined
          aria-label="Vernieuwen"
          size="small"
        />
      </div>

      <div class="facturen-grid">
        <button
          v-for="factuur in displayedFacturen"
          :key="factuur.id"
          type="button"
          class="factuur-card"
          @click="openFactuur(factuur.id)"
        >
          <div class="factuur-card-header">
            <div>
              <div class="factuur-nr">{{ factuur.factuurnr || 'Conceptfactuur' }}</div>
              <div class="factuur-opdrachtgever">
                {{ factuur.opdrachtgever?.display_name || factuur.opdrachtgever?.email || 'Onbekende opdrachtgever' }}
              </div>
            </div>
            <CustomTag
              :color="getStatusColor(factuur.status)"
              :label="getStatusLabel(factuur.status)"
            />
          </div>

          <div class="factuur-meta-grid">
            <div class="factuur-meta-item">
              <span class="factuur-meta-label">Factuurdatum</span>
              <span class="factuur-meta-value">{{ formatDate(factuur.factuur_datum) }}</span>
            </div>
            <div class="factuur-meta-item">
              <span class="factuur-meta-label">Vervaldatum</span>
              <span class="factuur-meta-value">{{ formatDate(factuur.vervaldatum) }}</span>
            </div>
            <div class="factuur-meta-item">
              <span class="factuur-meta-label">Inspectiebureau</span>
              <span class="factuur-meta-value">{{ factuur.ibedrijf?.users?.display_name || factuur.ibedrijf?.contactnaam || '-' }}</span>
            </div>
            <div class="factuur-meta-item">
              <span class="factuur-meta-label">Totaal</span>
              <span class="factuur-total">{{ formatCurrency(factuur.totaal) }}</span>
            </div>
          </div>
        </button>
      </div>

      <div
        v-if="hasMore"
        class="facturen-load-more"
        @click="loadMore"
      >
        <span class="facturen-load-more-text"> <i class="pi pi-arrow-down" /> Meer laden... </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .facturen-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  .filters-card {
    background: transparent;
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-background-200);
  }

  .filters-card :deep(.p-panel-header) {
    background: transparent;
    border-bottom: none;
    font-weight: 600;
    color: var(--p-background-800);
    padding: 0 0 0.75rem;
  }

  .filters-card :deep(.p-panel-content) {
    background: transparent;
    padding: 0;
  }

  .filters-card :deep(.p-panel-toggleable) {
    padding: 0;
  }

  .filters-stack {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .filters-search {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .filters-input {
    width: 100%;
  }

  .filters-meta {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-background-200);
  }

  .filters-select {
    flex: 1;
    min-width: 220px;
  }

  .filters-refresh {
    margin-left: auto;
  }

  .status-card {
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 1.25rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    background: var(--p-background-0);
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .facturen-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--p-background-200);
    margin-bottom: 1.25rem;
  }

  .facturen-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-600);
  }

  .facturen-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.25rem;
  }

  .factuur-card {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: left;
    background: linear-gradient(180deg, var(--p-background-0) 0%, var(--p-background-50) 100%);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .factuur-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .factuur-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .factuur-nr {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-900);
    word-break: break-word;
  }

  .factuur-opdrachtgever {
    margin-top: 0.35rem;
    font-size: 0.9rem;
    color: var(--p-background-600);
  }

  .factuur-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
    padding-top: 1rem;
    border-top: 1px solid var(--p-background-200);
  }

  .factuur-meta-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .factuur-meta-label {
    font-size: 0.75rem;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .factuur-meta-value {
    font-size: 0.95rem;
    color: var(--p-background-800);
  }

  .factuur-total {
    font-size: 1rem;
    font-weight: 700;
    color: var(--p-background-900);
  }

  .facturen-load-more {
    margin-top: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--p-background-200);
    text-align: center;
    cursor: pointer;
    color: var(--p-background-600);
    transition: color 0.2s ease;
  }

  .facturen-load-more:hover {
    color: var(--p-primary-500);
  }

  .facturen-load-more-text {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  @media (max-width: 640px) {
    .filters-refresh {
      margin-left: 0;
    }

    .facturen-grid {
      grid-template-columns: 1fr;
    }

    .factuur-card-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .factuur-meta-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
